import { useSyncedRef } from '@src/hooks/useSyncedRef';
import {
  isFixedRateLoanProductTemplate,
  isFloatingRateLoanProductTemplate,
  isOTCForwardProductTemplate,
  isOTCOptionProductTemplate,
  isOTCRepoProductTemplate,
} from '@src/types/OTC/otcSecurityTypeGuards';
import Big from 'big.js';
import { compact } from 'lodash-es';
import { useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { useCurrenciesContext } from '../contexts/CurrenciesContext';
import { useRecentSymbols } from '../contexts/RecentSymbolsContext';
import { SecuritiesContext } from '../contexts/SecuritiesContext';
import { useCachedSubscription } from '../hooks/useCachedSubscription';
import { useObservable, useObservableValue } from '../hooks/useObservable';
import { wsScanToMap } from '../pipes/wsScanToMap';
import { SECURITY } from '../tokens/requestTypes';
import {
  SecurityTypesUtils,
  type OTCFixedRateLoanProductTemplate,
  type OTCForwardProductTemplate,
  type RepresentativeOption,
  type SecurityTypes,
} from '../types';
import type {
  OTCFloatingRateLoanProductTemplate,
  OTCOptionProductTemplate,
  OTCRepoProductTemplate,
} from '../types/OTC/otcSecurityServerTypes';
import type { Security } from '../types/Security';
import { isExpired, supportsBPSPricing } from '../utils';
import { EMPTY_ARRAY } from '../utils/empty';

export const getCurrencyPairFromSecurity = (security: Security) => `${security.BaseCurrency}-${security.QuoteCurrency}`;

type HydratedSecurity = Security & { searchSymbol: string };

const SECURITY_REQUEST = { name: SECURITY, tag: 'SecuritiesProvider' } as const;

export const SecuritiesProvider = function SecuritiesProvider({ children }: PropsWithChildren) {
  const [isLoaded, setIsLoaded] = useState(false);
  const { data: subscription, isLoading } = useCachedSubscription<Security>(SECURITY_REQUEST);

  // Please discuss with wider team if the below newMapEachUpdate: true needs changing to false
  const securitiesBySymbolObs = useObservable(
    () =>
      subscription.pipe(
        wsScanToMap({ getUniqueKey: d => d.Symbol, newMapEachUpdate: true }),
        shareReplay({ refCount: false, bufferSize: 1 })
      ),
    [subscription]
  );

  const securitiesBySymbol = useObservableValue(() => securitiesBySymbolObs, [securitiesBySymbolObs], new Map());

  const securitiesListObs = useObservable(
    () =>
      securitiesBySymbolObs.pipe(
        map(securitiesBySymbol =>
          [...securitiesBySymbol.values()].filter(security => {
            // filter out expired securities
            if (isExpired(security)) {
              return false;
            }
            // filter out securities with EndTime (indicates it was soft deleted)
            if (security?.EndTime && new Date(security?.EndTime) < new Date()) {
              return false;
            }
            return true;
          })
        )
      ),
    [securitiesBySymbolObs]
  );

  const securitiesList = useObservableValue(
    () => securitiesListObs.pipe(map(secList => secList)),
    [securitiesListObs],
    EMPTY_ARRAY
  );

  const { recentSymbolsList } = useRecentSymbols();
  const { currenciesBySymbol } = useCurrenciesContext();

  const currenciesBySymbolRef = useSyncedRef(currenciesBySymbol);
  const hydratedSecuritiesObs = useObservable(
    () =>
      securitiesListObs.pipe(
        map(securitiesList => {
          const sorted = Array.from(securitiesList).toSorted(
            // sort by displaySymbol, recency is used later
            (a, b) => (a.DisplaySymbol ?? a.Symbol).localeCompare(b.DisplaySymbol ?? b.Symbol)
          );
          const list: HydratedSecurity[] = [];
          const bySymbol = new Map<string, HydratedSecurity>();
          for (const { Symbol, BaseCurrency, QuoteCurrency, Description, ...s } of sorted) {
            const sec = {
              ...s,
              Symbol,
              BaseCurrency,
              QuoteCurrency,
              searchSymbol: Symbol.replace('-', '').toLowerCase(),
              Description:
                Description ??
                (currenciesBySymbolRef.current != null
                  ? // todo
                    `${currenciesBySymbolRef.current.get(BaseCurrency ?? '')?.Description} - ${
                      currenciesBySymbolRef.current.get(QuoteCurrency)?.Description
                    }`
                  : ''),
            };
            list.push(sec);
            bySymbol.set(Symbol, sec);
          }
          return { list, bySymbol };
        }),
        shareReplay({ refCount: false, bufferSize: 1 })
      ),
    [securitiesListObs, currenciesBySymbolRef]
  );

  const sortedSearchableSecuritiesObs = useObservable(
    () =>
      hydratedSecuritiesObs.pipe(
        map(({ list, bySymbol }) => {
          // put the five recent symbols at the front of the list, remove them from the bigger list to avoid duplicates
          const recentSymbols = recentSymbolsList.slice(0, 5).reverse();
          const recentSecurities = compact(recentSymbols.map(symbol => bySymbol.get(symbol)));
          const recentSymbolsSet = new Set(recentSymbols);
          return recentSecurities.concat(list.filter(sec => !recentSymbolsSet.has(sec.Symbol)));
        })
      ),
    [hydratedSecuritiesObs, recentSymbolsList]
  );

  const searchableSecurities = useObservableValue(() => sortedSearchableSecuritiesObs, [sortedSearchableSecuritiesObs]);

  useEffect(() => {
    if (!isLoaded) {
      const hasLoadedNow = [securitiesList, securitiesBySymbol, searchableSecurities].every(dataset => dataset != null);
      if (hasLoadedNow) {
        setIsLoaded(hasLoadedNow);
      }
    }
  }, [securitiesList, securitiesBySymbol, searchableSecurities, isLoaded, isLoading]);

  const securitiesListSortedByRank = useMemo(
    () =>
      securitiesList ? securitiesList.toSorted((a, b) => ((a.Rank ?? Infinity) < (b.Rank ?? Infinity) ? -1 : 1)) : [],
    [securitiesList]
  );

  const perps = useMemo(() => getPerpsFromSecurities(securitiesList), [securitiesList]);
  const perpsByCurrencyPair = useMemo(() => getPerpsByCurrencyPair(perps), [perps]);

  const futures = useMemo(() => getFuturesFromSecurities(securitiesList), [securitiesList]);
  const futuresByCurrencyPair = useMemo(() => getFuturesByCurrencyPair(futures), [futures]);

  const otcSecuritiesValues = useMemo(() => getAllOTCSecuritiesFromSecurities(securitiesList), [securitiesList]);

  const futuresByMarketByCurrency = useMemo(() => getFuturesByMarketByCurrencyPair(futures), [futures]);

  const options = useMemo(() => {
    const now = new Date();
    const filteredOptions = securitiesList.filter((sec): sec is SecurityTypes['Option'] => {
      // if not option, filter it out
      if (!SecurityTypesUtils.Option.isType(sec)) {
        return false;
      }
      // filter out securities with EndTime (indicates it was soft deleted)
      if (sec?.EndTime && new Date(sec?.EndTime) < now) {
        return false;
      }
      // If expired filter it out
      if (new Date(sec.Expiration) < now) {
        return false;
      }

      return true;
    });
    return filteredOptions.toSorted((a, b) => Big(a.StrikePrice).cmp(b.StrikePrice));
  }, [securitiesList]);

  const expirationByMarketByCurrencyIdentity = useMemo(
    () => getExpirationByMarketByCurrencyIdentity(options),
    [options]
  );

  const optionSecurityBySymbol = useMemo(() => new Map(options.map(o => [o.Symbol, o])), [options]);
  const optionSecurityBySymbolObs = useObservable(() => of(optionSecurityBySymbol), [optionSecurityBySymbol]);
  const representativeOptionSecurityByKey: Map<string, RepresentativeOption> = useMemo(
    () =>
      new Map<string, RepresentativeOption>(
        options.map(o => [
          getRepresentativeOptionSecurityKey(o.BaseCurrency, o.Markets, o.UnderlyingCode),
          {
            SettlementCurrency: o.SettlementCurrency,
            UnderlyingQuoteCurrency: o.UnderlyingQuoteCurrency,
            UnderlyingSymbol: o.UnderlyingSymbol,
          },
        ])
      ),
    [options]
  );

  const strikesByOptionKey = useMemo(() => getStrikesByOptionKey(options), [options]);

  const { baseMap: spotTradingPairsByBaseCurrency, quoteMap: spotTradingPairsByQuoteCurrency } = useMemo(
    () => getSpotTradingPairsByCurrency(securitiesList),
    [securitiesList]
  );

  const hasBPSPricingSecurities = useMemo(() => securitiesList.some(supportsBPSPricing), [securitiesList]);

  return (
    <SecuritiesContext.Provider
      value={{
        isLoaded,
        hasBPSPricingSecurities,
        securitiesBySymbol: securitiesBySymbol!,
        securitiesBySymbolObs,
        securitiesList: securitiesList!,
        searchableSecurities: searchableSecurities!,
        securitiesListSortedByRank,
        perps,
        perpsByCurrencyPair,
        futures,
        futuresByCurrencyPair,
        futuresByMarketByCurrency,
        options,
        expirationByMarketByCurrencyIdentity,
        optionSecurityBySymbol,
        optionSecurityBySymbolObs,
        representativeOptionSecurityByKey,
        strikesByOptionKey,
        spotTradingPairsByBaseCurrency,
        spotTradingPairsByQuoteCurrency,
        otcFixedRateLoansByDisplaySymbol: otcSecuritiesValues.otcFixedRateLoansByDisplaySymbol,
        otcForwardsByDisplaySymbol: otcSecuritiesValues.otcForwardsByDisplaySymbol,
        otcOptionsByDisplaySymbol: otcSecuritiesValues.otcOptionsByDisplaySymbol,
        otcFloatingRateLoansByDisplaySymbol: otcSecuritiesValues.otcFloatingRateLoansByDisplaySymbol,
        otcReposByDisplaySymbol: otcSecuritiesValues.otcReposByDisplaySymbol,
      }}
    >
      {children}
    </SecuritiesContext.Provider>
  );
};

export function getPerpsFromSecurities(securitiesList: Security[]) {
  const now = new Date();
  return securitiesList.filter((sec): sec is SecurityTypes['Perp'] => {
    if (!SecurityTypesUtils.Perp.isType(sec)) {
      return false;
    }
    // filter out securities with EndTime (indicates it was soft deleted)
    if (sec?.EndTime && new Date(sec?.EndTime) < now) {
      return false;
    }
    return true;
  });
}

export function getPerpsByCurrencyPair(perps: SecurityTypes['Perp'][]) {
  const lookup = new Map<string, SecurityTypes['Perp'][]>();

  for (const perp of perps) {
    const currencyPair = getCurrencyPairFromSecurity(perp);

    const list = lookup.get(currencyPair)!;

    if (list) {
      list.push(perp);
    } else {
      lookup.set(currencyPair, [perp]);
    }
  }

  return lookup;
}

export function getFuturesFromSecurities(securitiesList: Security[]) {
  const now = new Date();
  return securitiesList.filter((sec): sec is SecurityTypes['Future'] => {
    if (!SecurityTypesUtils.Future.isType(sec)) {
      return false;
    }
    // filter out securities with EndTime (indicates it was soft deleted)
    if (sec?.EndTime && new Date(sec?.EndTime) < now) {
      return false;
    }
    // If expired filter it out
    if (new Date(sec.Expiration) < now) {
      return false;
    }
    return true;
  });
}

export function getFuturesByCurrencyPair(futures: SecurityTypes['Future'][]) {
  const lookup = new Map<string, SecurityTypes['Future'][]>();

  for (const future of futures) {
    const currencyPair = getCurrencyPairFromSecurity(future);

    const list = lookup.get(currencyPair)!;

    if (list) {
      list.push(future);
    } else {
      lookup.set(currencyPair, [future]);
    }
  }

  return lookup;
}

export function getFuturesByMarketByCurrencyPair(futures: SecurityTypes['Future'][]) {
  const futuresByMarketByCurrencyPair = new Map<string, Map<string, Set<SecurityTypes['Future']>>>();

  for (const future of futures) {
    const { Markets = EMPTY_ARRAY } = future;
    const currencyPair = getCurrencyPairFromSecurity(future);

    if (!futuresByMarketByCurrencyPair.has(currencyPair)) {
      futuresByMarketByCurrencyPair.set(currencyPair, new Map());
    }
    const expirationByMarket = futuresByMarketByCurrencyPair.get(currencyPair)!;

    for (const market of Markets) {
      const expirationSet = expirationByMarket.get(market);

      if (expirationSet) {
        expirationSet.add(future);
      } else {
        expirationByMarket.set(market, new Set<SecurityTypes['Future']>([future]));
      }
    }
  }

  return futuresByMarketByCurrencyPair;
}

// Maybe bad naming, assuming Key = Coin / Exchange / Expiry identifies, where Strike + Type are just parameters on that option
export function getStrikesByOptionKey(options: SecurityTypes['Option'][]) {
  const strikesByOptionKey = new Map<string, string[]>();
  for (const option of options) {
    const { UnderlyingCode, BaseCurrency, Markets = EMPTY_ARRAY, Expiration, StrikePrice } = option;
    const key = `${UnderlyingCode ?? BaseCurrency}/${Markets}/${Expiration}`;

    if (strikesByOptionKey.has(key)) {
      const copy = strikesByOptionKey.get(key)!.slice();
      copy.push(StrikePrice);
      strikesByOptionKey.set(key, copy);
    } else {
      strikesByOptionKey.set(key, [StrikePrice]);
    }
  }

  return strikesByOptionKey;
}

export function getExpirationByMarketByCurrencyIdentity(options: SecurityTypes['Option'][]) {
  const expirationByMarketByCurrencyIdentity = new Map<string, Map<string, Set<string>>>();

  for (const option of options) {
    const { UnderlyingCode, BaseCurrency, Expiration, Markets = EMPTY_ARRAY } = option;
    const key = UnderlyingCode ?? BaseCurrency;

    if (!expirationByMarketByCurrencyIdentity.has(key)) {
      expirationByMarketByCurrencyIdentity.set(key, new Map());
    }
    const expirationByMarket = expirationByMarketByCurrencyIdentity.get(key)!;

    for (const market of Markets) {
      const expirationSet = expirationByMarket.get(market);

      if (expirationSet) {
        expirationSet.add(Expiration);
      } else {
        expirationByMarket.set(market, new Set<string>([Expiration]));
      }
    }
  }

  return expirationByMarketByCurrencyIdentity;
}

export function getRepresentativeOptionSecurityKey(
  baseCurrency: string,
  markets: string[] | undefined,
  underlyingCode: string | undefined
) {
  return [underlyingCode ?? baseCurrency, (markets ?? []).join('_')].join('_');
}

/**
 * Builds two maps where every trading pair is indexed by their base currency and quote currency.
 * Filters the incoming securities list to only spot trading pairs so you can just send in the entire non-spot list too
 */
export function getSpotTradingPairsByCurrency(securitiesList: Security[]) {
  const baseMap = new Map<string, SecurityTypes['Spot'][]>();
  const quoteMap = new Map<string, SecurityTypes['Spot'][]>();
  const spotSecurities = securitiesList.filter(SecurityTypesUtils.Spot.isType);

  for (const security of spotSecurities) {
    const baseList = baseMap.get(security.BaseCurrency) ?? [];
    const quoteList = quoteMap.get(security.QuoteCurrency) ?? [];
    baseList.push(security);
    quoteList.push(security);
    baseMap.set(security.BaseCurrency, baseList);
    quoteMap.set(security.QuoteCurrency, quoteList);
  }

  return { baseMap, quoteMap };
}

export function getAllOTCSecuritiesFromSecurities(securitiesList: Security[]): {
  otcForwardsByDisplaySymbol: Map<string, OTCForwardProductTemplate>;
  otcFixedRateLoansByDisplaySymbol: Map<string, OTCFixedRateLoanProductTemplate>;
  otcOptionsByDisplaySymbol: Map<string, OTCOptionProductTemplate>;
  otcFloatingRateLoansByDisplaySymbol: Map<string, OTCFloatingRateLoanProductTemplate>;
  otcReposByDisplaySymbol: Map<string, OTCRepoProductTemplate>;
} {
  const otcForwards: OTCForwardProductTemplate[] = [];
  const otcFixedRateLoans: OTCFixedRateLoanProductTemplate[] = [];
  const otcOptions: OTCOptionProductTemplate[] = [];
  const otcFloatingRateLoans: OTCFloatingRateLoanProductTemplate[] = [];
  const otcRepos: OTCRepoProductTemplate[] = [];

  // Single pass through securitiesList
  for (const sec of securitiesList) {
    if (isOTCForwardProductTemplate(sec)) {
      otcForwards.push(sec);
    } else if (isFixedRateLoanProductTemplate(sec)) {
      otcFixedRateLoans.push(sec);
    } else if (isOTCOptionProductTemplate(sec)) {
      otcOptions.push(sec);
    } else if (isFloatingRateLoanProductTemplate(sec)) {
      otcFloatingRateLoans.push(sec);
    } else if (isOTCRepoProductTemplate(sec)) {
      otcRepos.push(sec);
    }
  }

  return {
    otcForwardsByDisplaySymbol: new Map(otcForwards.map(sec => [sec.DisplaySymbol, sec])),
    otcFixedRateLoansByDisplaySymbol: new Map(otcFixedRateLoans.map(sec => [sec.DisplaySymbol, sec])),
    otcOptionsByDisplaySymbol: new Map(otcOptions.map(sec => [sec.DisplaySymbol, sec])),
    otcFloatingRateLoansByDisplaySymbol: new Map(otcFloatingRateLoans.map(sec => [sec.DisplaySymbol, sec])),
    otcReposByDisplaySymbol: new Map(otcRepos.map(sec => [sec.DisplaySymbol, sec])),
  };
}
